import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LockScreenService } from './lock-screen.service';
import { TIMEOUTS } from '../constants/timeouts.constants';

@Injectable({
    providedIn: 'root',
})
export class InactivityService implements OnDestroy {
    private readonly INACTIVITY_TIMEOUT = TIMEOUTS.INACTIVITY;
    private destroy$ = new Subject<void>();
    private userActivity: Subscription | null = null;
    private inactivityTimer: Subscription | null = null;
    private lastActivityTime: number = Date.now();

    constructor(private lockScreenService: LockScreenService) {}

    startWatching(): void {
        this.setupActivityListeners();
        this.startInactivityTimer();
    }

    private setupActivityListeners(): void {
        const activity$ = merge(
            fromEvent(document, 'mousemove'),
            fromEvent(document, 'mousedown'),
            fromEvent(document, 'mouseup'),
            fromEvent(document, 'click'),
            fromEvent(document, 'keydown'),
            fromEvent(document, 'keyup'),
            fromEvent(document, 'touchstart'),
            fromEvent(document, 'touchend'),
            fromEvent(document, 'scroll'),
            fromEvent(document, 'wheel')
        ).pipe(debounceTime(100), takeUntil(this.destroy$));

        this.userActivity = activity$.subscribe(() => {
            this.lastActivityTime = Date.now();
        });
    }

    private startInactivityTimer(): void {
        this.inactivityTimer = timer(0, 500)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const currentTime = Date.now();
                const timeSinceLastActivity = currentTime - this.lastActivityTime;

                if (timeSinceLastActivity >= this.INACTIVITY_TIMEOUT) {
                    this.lockScreenService.lock();
                }
            });
    }

    stopWatching(): void {
        if (this.inactivityTimer) {
            this.inactivityTimer.unsubscribe();
        }
        if (this.userActivity) {
            this.userActivity.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopWatching();
    }
}
